import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// no lazy loading for auth pages to avoid flickering
const AuthLayout = React.lazy(() => import('@app/components/layouts/AuthLayout/AuthLayout'));
import LoginPage from '@app/pages/LoginPage';
import SignUpPage from '@app/pages/SignUpPage';
import ForgotPasswordPage from '@app/pages/ForgotPasswordPage';
import SecurityCodePage from '@app/pages/SecurityCodePage';
import NewPasswordPage from '@app/pages/NewPasswordPage';
import LockPage from '@app/pages/LockPage';

import MainLayout from '@app/components/layouts/main/MainLayout/MainLayout';
import RequireAuth from '@app/components/router/RequireAuth';
import { withLoading } from '@app/hocs/withLoading.hoc';
import OrderNewPage from '@app/pages/order/OrderNewPage';
import OrderDetailPage from '@app/pages/order/OrderDetailPage';
import OrderEditPage from '@app/pages/order/OrderEditPage';

import OrderReturnNewPage from '@app/pages/orderReturn/OrderReturnNewPage';
import OrderReturnEditPage from '@app/pages/orderReturn/OrderReturnEditPage';
import { readUser } from '@app/services/localStorage.service';
import { useUserRoles } from '@app/hooks/useUserRoles';
import { sidebarNavigation } from '@app/components/layouts/main/sider/sidebarNavigation';
import { buildNavAccess, resolveLandingPath } from '@app/components/layouts/main/sider/navigationAccess';
import { Loading } from '@app/components/common/Loading/Loading';

const BankPage = React.lazy(() => import('@app/pages/settings/BankPage'));
const GeneralPage = React.lazy(() => import('@app/pages/settings/GeneralPage'));
const UserPage = React.lazy(() => import('@app/pages/settings/UserPage'));
const UserDetailPage = React.lazy(() => import('@app/pages/settings/UserDetailPage'));
const RolePage = React.lazy(() => import('@app/pages/settings/RolePage'));
const PermissionPage = React.lazy(() => import('@app/pages/settings/PermissionPage'));
const SessionsPage = React.lazy(() => import('@app/pages/settings/SessionsPage'));
const AuditLogPage = React.lazy(() => import('@app/pages/settings/AuditLogPage'));
const MessagingChannelsPage = React.lazy(() => import('@app/pages/settings/MessagingChannelsPage'));
const AutoReplyRulesPage = React.lazy(() => import('@app/pages/settings/AutoReplyRulesPage'));
const InboxPage = React.lazy(() => import('@app/pages/messaging/InboxPage'));
const LedgerPage = React.lazy(() => import('@app/pages/ledger/LedgerPage'));
const LedgerDetailPage = React.lazy(() => import('@app/pages/ledger/LedgerDetailPage'));
const EmployeePage = React.lazy(() => import('@app/pages/employee/EmployeePage'));
const EmployeeDetailPage = React.lazy(() => import('@app/pages/employee/EmployeeDetailPage'));
const BonusPage = React.lazy(() => import('@app/pages/employee/BonusPage'));
const MySalaryPage = React.lazy(() => import('@app/pages/employee/MySalaryPage'));
const MyBonusPage = React.lazy(() => import('@app/pages/employee/MyBonusPage'));
const CustomerPage = React.lazy(() => import('@app/pages/partner/CustomerPage'));
const CustomerDetailPage = React.lazy(() => import('@app/pages/partner/CustomerDetailPage'));
const SupplierPage = React.lazy(() => import('@app/pages/partner/SupplierPage'));
const SupplierDetailPage = React.lazy(() => import('@app/pages/partner/SupplierDetailPage'));
const ProductPage = React.lazy(() => import('@app/pages/inventory/ProductPage'));
const ProductDetailPage = React.lazy(() => import('@app/pages/inventory/ProductDetailPage'));
const MovementPage = React.lazy(() => import('@app/pages/inventory/MovementPage'));
const MovementDetailPage = React.lazy(() => import('@app/pages/inventory/MovementDetailPage'));
const OrderPage = React.lazy(() => import('@app/pages/order/OrderPage'));
const OrderReturnPage = React.lazy(() => import('@app/pages/orderReturn/OrderReturnPage'));
const OrderReturnDetailPage = React.lazy(() => import('@app/pages/orderReturn/OrderReturnDetailPage'));

const SalaryPage = React.lazy(() => import('@app/pages/employee/SalaryPage'));
const TimesheetPage = React.lazy(() => import('@app/pages/employee/TimesheetPage'));
const LeaveRequestPage = React.lazy(() => import('@app/pages/employee/LeaveRequestPage'));
const LeaveCalendarPage = React.lazy(() => import('@app/pages/employee/LeaveCalendarPage'));

const DashboardPage = React.lazy(() => import('@app/pages/report/DashboardPage'));
const ReportEndDayPage = React.lazy(() => import('@app/pages/report/ReportEndDayPage'));
const ReportOrderPage = React.lazy(() => import('@app/pages/report/ReportOrderPage'));
const ReportProductPage = React.lazy(() => import('@app/pages/report/ReportProductPage'));
const ReportStockPage = React.lazy(() => import('@app/pages/report/ReportStockPage'));
const GuidePage = React.lazy(() => import('@app/pages/help/GuidePage'));
const ReleaseLogPage = React.lazy(() => import('@app/pages/help/ReleaseLogPage'));
const ReportCustomerPage = React.lazy(() => import('@app/pages/report/ReportCustomerPage'));
const ReportMarketingPage = React.lazy(() => import('@app/pages/report/ReportMarketingPage'));
const MarketingAdSpendPage = React.lazy(() => import('@app/pages/marketing/MarketingAdSpendPage'));
const DeliveryTripPage = React.lazy(() => import('@app/pages/deliveryTrip/DeliveryTripPage'));
const DeliveryTripNewPage = React.lazy(() => import('@app/pages/deliveryTrip/DeliveryTripNewPage'));
const DeliveryTripDetailPage = React.lazy(() => import('@app/pages/deliveryTrip/DeliveryTripDetailPage'));
const QuickOrderPage = React.lazy(() => import('@app/pages/order/QuickOrderPage'));
const ScanOrderPage = React.lazy(() => import('@app/pages/order/ScanOrderPage'));
const ReportSupplierPage = React.lazy(() => import('@app/pages/report/ReportSupplierPage'));
const ReportChannelPage = React.lazy(() => import('@app/pages/report/ReportChannelPage'));
const ReportFinancePage = React.lazy(() => import('@app/pages/report/ReportFinancePage'));
const ZaloMessagePage = React.lazy(() => import('@app/pages/report/ZaloMessagePage'));

const ServerErrorPage = React.lazy(() => import('@app/pages/ServerErrorPage'));
const Error404Page = React.lazy(() => import('@app/pages/Error404Page'));
const Error403Page = React.lazy(() => import('@app/pages/Error403Page'));
const Logout = React.lazy(() => import('./Logout'));
const ProfilePage = React.lazy(() => import('@app/pages/ProfilePage'));
const SalesDashboardPage = React.lazy(() => import('@app/pages/SalesDashboard/SalesDashboardPage'));

export const DASHBOARD_PATH = '/';

//My custom app
const Employees = withLoading(EmployeePage);
const EmployeeDetail = withLoading(EmployeeDetailPage);
const Bonus = withLoading(BonusPage);
const MySalary = withLoading(MySalaryPage);
const MyBonus = withLoading(MyBonusPage);
const Customers = withLoading(CustomerPage);
const CustomerDetail = withLoading(CustomerDetailPage);
const Suppliers = withLoading(SupplierPage);
const SupplierDetail = withLoading(SupplierDetailPage);
const GeneralSettings = withLoading(GeneralPage);
const Banks = withLoading(BankPage);
const Users = withLoading(UserPage);
const UserDetail = withLoading(UserDetailPage);
const Roles = withLoading(RolePage);
const Permissions = withLoading(PermissionPage);
const Sessions = withLoading(SessionsPage);
const AuditLog = withLoading(AuditLogPage);
const MessagingChannels = withLoading(MessagingChannelsPage);
const AutoReplyRulesSettings = withLoading(AutoReplyRulesPage);
const Inbox = withLoading(InboxPage);
const Ledgers = withLoading(LedgerPage);
const LedgerDetail = withLoading(LedgerDetailPage);
const Products = withLoading(ProductPage);
const ProductDetail = withLoading(ProductDetailPage);
const Movement = withLoading(MovementPage);
const MovementDetail = withLoading(MovementDetailPage);

const Order = withLoading(OrderPage);
const OrderDetail = withLoading(OrderDetailPage);
const OrderNew = withLoading(OrderNewPage);
const OrderEdit = withLoading(OrderEditPage);

const OrderReturnNew = withLoading(OrderReturnNewPage);
const OrderReturnEdit = withLoading(OrderReturnEditPage);
const OrderReturn = withLoading(OrderReturnPage);
const OrderReturnDetail = withLoading(OrderReturnDetailPage);

const Salaries = withLoading(SalaryPage);
const Timesheets = withLoading(TimesheetPage);
const LeaveRequests = withLoading(LeaveRequestPage);
const LeaveCalendar = withLoading(LeaveCalendarPage);

const ReportDashboard = withLoading(DashboardPage);
const ReportEndDay = withLoading(ReportEndDayPage);
const ReportOrder = withLoading(ReportOrderPage);
const ReportProduct = withLoading(ReportProductPage);
const ReportStock = withLoading(ReportStockPage);
const Guide = withLoading(GuidePage);
const ReleaseLog = withLoading(ReleaseLogPage);
const ReportCustomer = withLoading(ReportCustomerPage);
const ReportMarketing = withLoading(ReportMarketingPage);
const MarketingAdSpends = withLoading(MarketingAdSpendPage);
const DeliveryTrips = withLoading(DeliveryTripPage);
const DeliveryTripNew = withLoading(DeliveryTripNewPage);
const DeliveryTripDetail = withLoading(DeliveryTripDetailPage);
const QuickOrder = withLoading(QuickOrderPage);
const ScanOrder = withLoading(ScanOrderPage);
const ReportSupplier = withLoading(ReportSupplierPage);
const ReportChannel = withLoading(ReportChannelPage);
const ReportFinance = withLoading(ReportFinancePage);
const ZaloMessages = withLoading(ZaloMessagePage);
//end custom

const ServerError = withLoading(ServerErrorPage);
const Error404 = withLoading(Error404Page);
const Error403 = withLoading(Error403Page);

// Profile
const Profile = withLoading(ProfilePage);
const SalesDashboard = withLoading(SalesDashboardPage);

const AuthLayoutFallback = withLoading(AuthLayout);
const LogoutFallback = withLoading(Logout);

/**
 * Trang chủ sau khi đăng nhập.
 *
 * Phải đợi biết chắc vai trò rồi mới chọn (localStorage có thể chưa có `roles`).
 * User không có quyền Tổng quan được chuyển sang menu đầu tiên họ được phép,
 * thay vì mở dashboard rồi dính 403.
 */
const HomeRoute: React.FC = () => {
  const { rolesLoaded } = useUserRoles();

  if (!rolesLoaded) return <Loading />;

  const access = buildNavAccess(readUser());
  if (access.home === 'sales') return <SalesDashboard />;
  if (access.home === 'main') return <ReportDashboard />;

  return <Navigate to={resolveLandingPath(sidebarNavigation, access)} replace />;
};

export const AppRouter: React.FC = () => {
  const protectedLayout = (
    <RequireAuth>
      <MainLayout />
    </RequireAuth>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path={DASHBOARD_PATH} element={protectedLayout}>
          <Route index element={<HomeRoute />} />

          {/* Customize here */}
          <Route path="*" element={<Error404 />}></Route>

          <Route path="inventory">
            <Route path="products">
              <Route path="list" element={<Products />} />
              <Route path=":id" element={<ProductDetail />} />
            </Route>
            <Route path="movements">
              <Route path="list" element={<Movement />} />
              <Route path=":id" element={<MovementDetail />} />
            </Route>
          </Route>
          <Route path="orders">
            <Route path="list" element={<Order />} />
            <Route path=":id" element={<OrderDetail />} />
            <Route path="new" element={<OrderNew />} />
            <Route path=":id/edit" element={<OrderEdit />} />
          </Route>
          <Route path="inbox">
            <Route index element={<Inbox />} />
            <Route path=":conversationId" element={<Inbox />} />
          </Route>
          <Route path="delivery-trips">
            <Route index element={<DeliveryTrips />} />
            <Route path="new" element={<DeliveryTripNew />} />
            <Route path=":id" element={<DeliveryTripDetail />} />
          </Route>
          {/* Đích của mã QR trên bill/tem + trang quét mã (tối ưu cho điện thoại). */}
          <Route path="o/:code" element={<QuickOrder />} />
          <Route path="scan" element={<ScanOrder />} />
          <Route path="order-returns">
            <Route path="list" element={<OrderReturn />} />
            <Route path=":id" element={<OrderReturnDetail />} />
            <Route path="new" element={<OrderReturnNew />} />
            <Route path=":id/edit" element={<OrderReturnEdit />} />
          </Route>
          <Route path="partners">
            <Route path="customers">
              <Route path="list" element={<Customers />} />
              <Route path=":id" element={<CustomerDetail />} />
            </Route>
            <Route path="suppliers">
              <Route path="list" element={<Suppliers />} />
              <Route path=":id" element={<SupplierDetail />} />
            </Route>
          </Route>
          <Route path="employees">
            <Route path="list" element={<Employees />} />
            <Route path=":id" element={<EmployeeDetail />} />
            <Route path="salaries" element={<Salaries />} />
            <Route path="timesheets" element={<Timesheets />} />
            <Route path="leave-requests" element={<LeaveRequests />} />
            <Route path="leave-calendar" element={<LeaveCalendar />} />
            <Route path="bonus" element={<Bonus />} />
          </Route>
          <Route path="ledgers">
            <Route path="list" element={<Ledgers />} />
            <Route path=":id" element={<LedgerDetail />} />
          </Route>
          <Route path="reports">
            <Route path="dashboard" element={<ReportDashboard />} />
            <Route path="end-of-last-day" element={<ReportEndDay />} />
            <Route path="order" element={<ReportOrder />} />
            <Route path="product" element={<ReportProduct />} />
            <Route path="stock" element={<ReportStock />} />
            <Route path="customer" element={<ReportCustomer />} />
            <Route path="marketing" element={<ReportMarketing />} />
            <Route path="supplier" element={<ReportSupplier />} />
            <Route path="channel" element={<ReportChannel />} />
            <Route path="finance" element={<ReportFinance />} />
            <Route path="zalo-messages" element={<ZaloMessages />} />
          </Route>
          <Route path="marketing">
            <Route path="ad-spends" element={<MarketingAdSpends />} />
          </Route>
          <Route path="settings">
            <Route path="generals" element={<GeneralSettings />} />
            <Route path="banks" element={<Banks />} />

            <Route path="users" element={<Users />} />
            <Route path="users/:id" element={<UserDetail />} />

            <Route path="roles" element={<Roles />} />
            <Route path="permissions" element={<Permissions />} />
            <Route path="sessions" element={<Sessions />} />
            <Route path="audit-log" element={<AuditLog />} />
            <Route path="messaging-channels" element={<MessagingChannels />} />
            <Route path="messaging-auto-replies" element={<AutoReplyRulesSettings />} />
          </Route>
          <Route path="help">
            <Route path="guide" element={<Guide />} />
            <Route path="releases" element={<ReleaseLog />} />
          </Route>
          {/* End customize here */}

          <Route path="server-error" element={<ServerError />} />
          <Route path="404" element={<Error404 />} />
          <Route path="403" element={<Error403 />} />
          <Route path="profile" element={<Profile />} />
          <Route path="my-salary" element={<MySalary />} />
          <Route path="my-bonus" element={<MyBonus />} />
          <Route path="sales-dashboard" element={<SalesDashboard />} />
        </Route>
        <Route path="/auth" element={<AuthLayoutFallback />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="sign-up" element={<SignUpPage />} />
          <Route
            path="lock"
            element={
              <RequireAuth>
                <LockPage />
              </RequireAuth>
            }
          />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="security-code" element={<SecurityCodePage />} />
          <Route path="new-password" element={<NewPasswordPage />} /> 
        </Route>
        <Route path="/logout" element={<LogoutFallback />} />
      </Routes>
    </BrowserRouter>
  );
};
